import React, { useEffect, useState, useMemo } from "react";
import { getVenues } from "@/api/venues";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VenueForm from "@/pages/admin/clients/VenueForm";

export default function ClientsIndex() {
    const [venues, setVenues] = useState([]);
    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        const fetchVenues = async () => {
            const data = await getVenues();
            if (Array.isArray(data)) setVenues(data);
        };
        fetchVenues();
    }, []);

    const sortedVenues = useMemo(() => {
        const sorted = [...venues];
        const { key, direction } = sortConfig;
        sorted.sort((a, b) => {
            const valA = a[key]?.toLowerCase?.() || "";
            const valB = b[key]?.toLowerCase?.() || "";
            if (valA < valB) return direction === "asc" ? -1 : 1;
            if (valA > valB) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return sorted.filter((venue) =>
            venue.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [venues, search, sortConfig]);

    const handleSort = (column) => {
        setSortConfig((prev) => ({
            key: column,
            direction: prev.key === column && prev.direction === "asc" ? "desc" : "asc"
        }));
    };

    const renderSortIcon = (column) => {
        if (sortConfig.key !== column) return null;
        return sortConfig.direction === "asc" ? (
            <ChevronUp className="ml-1 h-4 w-4 inline-block" />
        ) : (
            <ChevronDown className="ml-1 h-4 w-4 inline-block" />
        );
    };

    return (
        <div className="bg-white shadow rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-semibold text-[#5D1F42]">Clients</h1>
                <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 bg-[#5D1F42] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#712a55] transition"
                >
                    <Plus className="w-4 h-4" />
                    Add New Venue
                </button>
            </div>

            <Input
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-4"
            />

            <div className="overflow-auto">
                <table className="min-w-full border border-gray-200 text-sm rounded-xl overflow-hidden">
                    <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                    <tr>
                        <th
                            className="px-4 py-3 border-b cursor-pointer select-none hover:text-[#5D1F42]"
                            onClick={() => handleSort("name")}
                        >
                            <div className="flex items-center">
                                Name
                                {renderSortIcon("name")}
                            </div>
                        </th>
                        <th className="px-4 py-3 border-b text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {sortedVenues.map((venue) => (
                        <tr key={venue.id} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-2">{venue.name}</td>
                            <td className="px-4 py-2 text-right">
                                <Link
                                    to={`/admin/venues/${venue.id}`}
                                    className="text-sm text-[#5D1F42] hover:underline"
                                >
                                    View
                                </Link>
                            </td>
                        </tr>
                    ))}
                    {sortedVenues.length === 0 && (
                        <tr>
                            <td colSpan="2" className="px-4 py-2 text-gray-500 italic">No matching clients found.</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add New Venue</DialogTitle>
                    </DialogHeader>
                    <VenueForm />
                </DialogContent>
            </Dialog>
        </div>
    );
}
